import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Pencil, 
  Save, 
  X, 
  Upload, 
  Image as ImageIcon,
  Type,
  Link as LinkIcon,
  Palette,
  Eye,
  EyeOff,
  Settings,
  Check,
  Trash2,
  Undo2,
  Redo2,
  History,
  Clock,
  ChevronRight,
  FileText,
  AlertCircle,
  CheckCircle,
  RotateCcw,
  LogOut,
  User
} from 'lucide-react';
import { toast } from 'sonner';

// Use window.location.origin so API calls always match the current domain
// This works on all deployments (preview, production, custom domains) because
// Kubernetes ingress routes /api/* to the backend automatically
const API_URL = window.location.origin;

// Admin Context for global state
const AdminContext = createContext({
  isAdmin: false,
  isEditMode: false,
  setEditMode: () => {},
  sectionContent: {},
  updateSectionContent: () => {},
  pendingChanges: [],
  addPendingChange: () => {},
  clearPendingChanges: () => {},
  publishAllChanges: () => {},
  history: [],
  undo: () => {},
  redo: () => {},
  canUndo: false,
  canRedo: false,
  showPreview: false,
  setShowPreview: () => {},
  adminEmails: [],
  checkIsAdmin: () => false,
});

export const useAdmin = () => useContext(AdminContext);

// Admin Provider Component with History
export const AdminProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  // Hardcoded admin emails as fallback when backend is unreachable
  const FALLBACK_ADMIN_EMAILS = ['me@iamnaomithomas.com', 'naomi@techbaenae.com'];
  const [adminEmails, setAdminEmails] = useState(FALLBACK_ADMIN_EMAILS);
  const [isEditMode, setEditMode] = useState(false);
  const [sectionContent, setSectionContent] = useState({});
  const [pendingChanges, setPendingChanges] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showPreview, setShowPreview] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);

  // Try to fetch admin emails from backend, fallback to hardcoded list
  useEffect(() => {
    fetch(`${API_URL}/api/admin/emails`)
      .then(res => res.json())
      .then(data => {
        if (data.emails && data.emails.length > 0) {
          setAdminEmails(data.emails);
        }
      })
      .catch(() => {
        console.log('[AdminProvider] Using fallback admin emails');
      });
  }, []);

  // Check current user - prioritize localStorage for reliability
  useEffect(() => {
    // First check localStorage (most reliable, works offline)
    const savedEmail = localStorage.getItem('soiree_email');
    if (savedEmail) {
      console.log('[AdminProvider] Found email in localStorage:', savedEmail);
      setCurrentUserEmail(savedEmail);
    }
    
    // Then try OAuth session (may fail due to CORS)
    const headers = {};
    const storedToken = localStorage.getItem('auth_session_token');
    if (storedToken) {
      headers['Authorization'] = `Bearer ${storedToken}`;
    }
    
    fetch(`${API_URL}/api/auth/me`, {
      credentials: 'include',
      headers,
    })
      .then(res => {
        if (res.ok) return res.json();
        return null;
      })
      .then(data => {
        if (data && data.email) {
          console.log('[AdminProvider] OAuth user found:', data.email);
          setCurrentUserEmail(data.email);
          localStorage.setItem('soiree_email', data.email);
          if (data.is_admin) {
            localStorage.setItem('soiree_approved', 'true');
          }
        }
      })
      .catch((err) => {
        console.log('[AdminProvider] OAuth check failed, using localStorage');
      });
  }, []);

  // Check admin status when emails are loaded or user changes
  useEffect(() => {
    if (currentUserEmail) {
      const isUserAdmin = adminEmails.some(
        email => email.toLowerCase() === currentUserEmail.toLowerCase()
      );
      console.log('[AdminProvider] Admin check:', currentUserEmail, 'isAdmin:', isUserAdmin);
      setIsAdmin(isUserAdmin);
    }
  }, [adminEmails, currentUserEmail]);

  // Helper function to check if an email is admin
  const checkIsAdmin = useCallback((email) => {
    if (!email) return false;
    return adminEmails.some(e => e.toLowerCase() === email.toLowerCase());
  }, [adminEmails]);

  // Check for token in URL hash (passed from magic link) and save to localStorage
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('token=')) {
      const token = hash.split('token=')[1]?.split('&')[0];
      if (token) {
        localStorage.setItem('auth_session_token', token);
        console.log('[AdminProvider] Token received from URL hash and saved');
        // Clean up URL
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, []);

  // Helper function to fix bad Cloudinary URLs
  const fixCloudinaryUrl = (url) => {
    if (typeof url !== 'string') return url;
    const CORRECT_CLOUD = 'dkh2svcxu';
    const WRONG_CLOUDS = ['dkh2svcxuu', 'dko2ycxu'];  // Only actually wrong names (removed dkh2svcx - it's a prefix of the correct name)
    for (const wrong of WRONG_CLOUDS) {
      if (url.includes(`res.cloudinary.com/${wrong}/`)) {
        return url.replace(`res.cloudinary.com/${wrong}/`, `res.cloudinary.com/${CORRECT_CLOUD}/`);
      }
    }
    return url;
  };

  // Fix all URLs in a section object
  const fixSectionUrls = (section) => {
    const fixed = { ...section };
    for (const key of Object.keys(fixed)) {
      if (typeof fixed[key] === 'string' && fixed[key].includes('cloudinary')) {
        fixed[key] = fixCloudinaryUrl(fixed[key]);
      }
    }
    return fixed;
  };

  // Clear bad localStorage data on first load
  useEffect(() => {
    // Remove all dd_section_ keys that have bad cloudinary URLs
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('dd_section_')) {
        const value = localStorage.getItem(key);
        if (value && (value.includes('dkh2svcxuu') || value.includes('dko2ycxu'))) {
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach(key => {
      console.log('[AdminProvider] Removing bad localStorage entry:', key);
      localStorage.removeItem(key);
    });
  }, []);

  // Load all section content from localStorage first (for reliability)
  useEffect(() => {
    const loadedContent = {};
    // Iterate through localStorage and load all dd_section_ keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('dd_section_')) {
        try {
          const sectionId = key.replace('dd_section_', '');
          const data = JSON.parse(localStorage.getItem(key));
          // Fix any bad URLs before using
          loadedContent[sectionId] = fixSectionUrls(data);
        } catch (e) {
          console.error('Failed to parse localStorage section:', key);
        }
      }
    }
    if (Object.keys(loadedContent).length > 0) {
      setSectionContent(prev => ({ ...loadedContent, ...prev }));
      console.log('[AdminProvider] Loaded sections from localStorage:', Object.keys(loadedContent));
    }
  }, []);

  // Load all section content from backend for ALL visitors (not just admins)
  // This ensures public visitors see the same content that admins have saved
  useEffect(() => {
    fetch(`${API_URL}/api/admin/sections`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        const contentMap = {};
        (data.sections || []).forEach(section => {
          // Fix any bad URLs from backend
          contentMap[section.section_id] = fixSectionUrls(section);
        });
        // Backend content takes precedence over localStorage for public visitors
        setSectionContent(prev => ({ ...prev, ...contentMap }));
        console.log('[AdminProvider] Loaded sections from backend:', Object.keys(contentMap));
        
        // Cache ALL section content to localStorage so public visitors 
        // have a fallback if the backend goes down
        try {
          Object.entries(contentMap).forEach(([sectionId, data]) => {
            localStorage.setItem(`dd_section_${sectionId}`, JSON.stringify(data));
          });
          console.log('[AdminProvider] Cached sections to localStorage for resilience');
        } catch (e) {
          // localStorage might be full or disabled - non-critical
        }
      })
      .catch((err) => {
        console.error(`[AdminProvider] Backend sections load failed: ${err.message}. API_URL: ${API_URL}`);
        console.log('[AdminProvider] Using localStorage fallback if available');
      });
  }, []);

  // Load history from backend (admin only)
  useEffect(() => {
    if (isAdmin) {
      fetch(`${API_URL}/api/admin/history`)
        .then(res => res.json())
        .then(data => {
          setHistory(data.history || []);
        })
        .catch(console.error);
    }
  }, [isAdmin]);

  // Add a pending change (doesn't save immediately)
  const addPendingChange = useCallback((sectionId, field, oldValue, newValue) => {
    const change = {
      id: `${sectionId}-${field}-${Date.now()}`,
      sectionId,
      field,
      oldValue,
      newValue,
      timestamp: new Date().toISOString(),
    };
    
    setPendingChanges(prev => {
      // Replace existing change for same section/field
      const filtered = prev.filter(c => !(c.sectionId === sectionId && c.field === field));
      return [...filtered, change];
    });
  }, []);

  // Clear all pending changes
  const clearPendingChanges = useCallback(() => {
    setPendingChanges([]);
  }, []);

  // Publish all pending changes
  const publishAllChanges = useCallback(async () => {
    const changesBySection = {};
    
    // Group changes by section
    pendingChanges.forEach(change => {
      if (!changesBySection[change.sectionId]) {
        changesBySection[change.sectionId] = {};
      }
      changesBySection[change.sectionId][change.field] = change.newValue;
    });

    // Save each section
    const sessionToken = localStorage.getItem('auth_session_token');
    const savePromises = Object.entries(changesBySection).map(async ([sectionId, updates]) => {
      try {
        const headers = { 'Content-Type': 'application/json' };
        // Add Authorization header with session token if available
        if (sessionToken) {
          headers['Authorization'] = `Bearer ${sessionToken}`;
        }
        
        const response = await fetch(`${API_URL}/api/admin/sections`, {
          method: 'POST',
          headers,
          credentials: 'include', // Also send session cookie
          body: JSON.stringify({
            section_id: sectionId,
            ...updates,
          }),
        });
        
        if (response.ok) {
          const data = await response.json();
          return { sectionId, data, success: true };
        } else if (response.status === 401 || response.status === 403) {
          console.error('Authentication required to save content. Please re-login via magic link.');
          return { sectionId, success: false, authError: true };
        }
      } catch (error) {
        console.error(`Failed to update section ${sectionId}:`, error);
      }
      return { sectionId, success: false };
    });

    const results = await Promise.all(savePromises);
    
    // Update local state with successful saves
    results.forEach(result => {
      if (result.success) {
        setSectionContent(prev => ({
          ...prev,
          [result.sectionId]: result.data,
        }));
      }
    });

    // Add to history
    if (pendingChanges.length > 0) {
      const historyEntry = {
        id: `history-${Date.now()}`,
        timestamp: new Date().toISOString(),
        changes: [...pendingChanges],
        type: 'publish',
      };
      
      // Save to backend
      fetch(`${API_URL}/api/admin/history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(historyEntry),
      }).catch(console.error);
      
      setHistory(prev => [...prev, historyEntry]);
      setHistoryIndex(prev => prev + 1);
    }

    // Clear pending changes
    setPendingChanges([]);
    
    return results.every(r => r.success);
  }, [pendingChanges]);

  // Update section content (immediate save for backward compatibility)
  const updateSectionContent = useCallback(async (sectionId, updates) => {
    // Always save to localStorage first as a reliable fallback
    const localStorageKey = `dd_section_${sectionId}`;
    try {
      const existingData = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
      const newData = { ...existingData, ...updates };
      localStorage.setItem(localStorageKey, JSON.stringify(newData));
      
      // Update state immediately with localStorage data
      setSectionContent(prev => ({
        ...prev,
        [sectionId]: { ...(prev[sectionId] || {}), ...updates },
      }));
    } catch (e) {
      console.error('Failed to save to localStorage:', e);
    }
    
    // Also try to save to backend
    try {
      const sessionToken = localStorage.getItem('auth_session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      const response = await fetch(`${API_URL}/api/admin/sections`, {
        method: 'POST',
        headers,
        credentials: 'include', // Also send session cookie
        body: JSON.stringify({
          section_id: sectionId,
          ...updates,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setSectionContent(prev => ({
          ...prev,
          [sectionId]: data,
        }));
        console.log(`[Admin] Content saved to backend for section: ${sectionId}`);
        toast.success('Changes saved successfully');
        return true;
      } else if (response.status === 401 || response.status === 403) {
        console.warn('Authentication required to save content to backend.');
        toast.error('Session expired. Please re-login via magic link: /auth/magic?key=dd2026admin');
        return false;
      } else {
        console.error('Backend save failed with status:', response.status);
        toast.error('Failed to save to server. Changes saved locally only.');
        return false;
      }
    } catch (error) {
      console.error(`Backend save failed: ${error.message}. API_URL: ${API_URL}`);
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        toast.error(`Cannot reach backend at ${API_URL}. Check your deployment's REACT_APP_BACKEND_URL setting.`);
      } else {
        toast.error(`Save failed: ${error.message}`);
      }
      return false;
    }
  }, []);

  // Undo last change
  const undo = useCallback(async () => {
    if (historyIndex < 0 || history.length === 0) return;
    
    const lastEntry = history[historyIndex];
    if (!lastEntry) return;

    // Revert changes
    for (const change of lastEntry.changes) {
      await updateSectionContent(change.sectionId, { [change.field]: change.oldValue });
    }
    
    setHistoryIndex(prev => prev - 1);
  }, [history, historyIndex, updateSectionContent]);

  // Redo last undone change
  const redo = useCallback(async () => {
    if (historyIndex >= history.length - 1) return;
    
    const nextEntry = history[historyIndex + 1];
    if (!nextEntry) return;

    // Re-apply changes
    for (const change of nextEntry.changes) {
      await updateSectionContent(change.sectionId, { [change.field]: change.newValue });
    }
    
    setHistoryIndex(prev => prev + 1);
  }, [history, historyIndex, updateSectionContent]);

  const canUndo = historyIndex >= 0;
  const canRedo = historyIndex < history.length - 1;

  return (
    <AdminContext.Provider value={{
      isAdmin,
      isEditMode,
      setEditMode,
      sectionContent,
      updateSectionContent,
      pendingChanges,
      addPendingChange,
      clearPendingChanges,
      publishAllChanges,
      history,
      undo,
      redo,
      canUndo,
      canRedo,
      showPreview,
      setShowPreview,
      adminEmails,
      checkIsAdmin,
    }}>
      {children}
    </AdminContext.Provider>
  );
};

// Preview Panel Component
export const PreviewPanel = ({ onClose }) => {
  const { 
    isAdmin, 
    pendingChanges, 
    publishAllChanges, 
    clearPendingChanges,
    history,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useAdmin();
  
  const [isPublishing, setIsPublishing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Calculate historyIndex locally based on canUndo state
  const historyIndex = canUndo ? history.length - 1 : -1;

  if (!isAdmin) return null;

  const handlePublish = async () => {
    setIsPublishing(true);
    await publishAllChanges();
    setIsPublishing(false);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      className="fixed right-0 top-0 bottom-0 w-80 bg-gray-900 border-l border-white/10 shadow-2xl z-[60] flex flex-col"
    >
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Eye className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-white font-semibold text-sm">Preview Changes</h2>
            <p className="text-white/50 text-xs">{pendingChanges.length} pending</p>
          </div>
        </div>
        <button
          onClick={(e) => { 
            e.preventDefault();
            e.stopPropagation(); 
            if (onClose) onClose();
          }}
          className="p-2 rounded-lg hover:bg-white/20 transition-colors cursor-pointer z-50"
          type="button"
          data-testid="preview-panel-close-btn"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10">
        <button
          onClick={() => setShowHistory(false)}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            !showHistory ? 'text-purple-400 border-b-2 border-purple-400' : 'text-white/50 hover:text-white/80'
          }`}
        >
          Pending ({pendingChanges.length})
        </button>
        <button
          onClick={() => setShowHistory(true)}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            showHistory ? 'text-purple-400 border-b-2 border-purple-400' : 'text-white/50 hover:text-white/80'
          }`}
        >
          History ({history.length})
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {!showHistory ? (
          // Pending Changes View
          pendingChanges.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-white/5 mx-auto mb-4 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-white/70">No pending changes</p>
              <p className="text-white/40 text-sm mt-1">All changes have been published</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingChanges.map((change) => (
                <motion.div
                  key={change.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-xl bg-white/5 border border-white/10"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-500/20 text-purple-300 uppercase">
                        {change.sectionId}
                      </span>
                      <span className="text-white/40 text-xs">{change.field}</span>
                    </div>
                    <span className="text-white/30 text-xs">{formatTime(change.timestamp)}</span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-red-400 text-xs mt-1">−</span>
                      <p className="text-red-300/70 text-sm line-through truncate flex-1">
                        {change.oldValue || '(empty)'}
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-green-400 text-xs mt-1">+</span>
                      <p className="text-green-300 text-sm truncate flex-1">
                        {change.newValue || '(empty)'}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )
        ) : (
          // History View
          history.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-white/5 mx-auto mb-4 flex items-center justify-center">
                <History className="w-8 h-8 text-white/30" />
              </div>
              <p className="text-white/70">No history yet</p>
              <p className="text-white/40 text-sm mt-1">Changes will appear here after publishing</p>
            </div>
          ) : (
            <div className="space-y-3">
              {[...history].reverse().map((entry, idx) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-xl border ${
                    history.length - 1 - idx <= historyIndex 
                      ? 'bg-purple-500/10 border-purple-500/30' 
                      : 'bg-white/5 border-white/10 opacity-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-white/40" />
                      <span className="text-white/60 text-sm">{formatDate(entry.timestamp)}</span>
                      <span className="text-white/40 text-xs">{formatTime(entry.timestamp)}</span>
                    </div>
                    <span className="text-white/30 text-xs">{entry.changes.length} changes</span>
                  </div>
                  
                  <div className="space-y-1">
                    {entry.changes.slice(0, 3).map((change, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="px-1.5 py-0.5 rounded bg-white/10 text-white/50">{change.sectionId}</span>
                        <span className="text-white/40">{change.field}</span>
                        <ChevronRight className="w-3 h-3 text-white/20" />
                        <span className="text-white/70 truncate">{change.newValue}</span>
                      </div>
                    ))}
                    {entry.changes.length > 3 && (
                      <p className="text-white/30 text-xs">+{entry.changes.length - 3} more</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t border-white/10 space-y-3">
        {/* Undo/Redo buttons */}
        <div className="flex gap-2">
          <button
            onClick={undo}
            disabled={!canUndo}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg transition-colors ${
              canUndo 
                ? 'bg-white/10 text-white hover:bg-white/20' 
                : 'bg-white/5 text-white/30 cursor-not-allowed'
            }`}
          >
            <Undo2 className="w-4 h-4" />
            Undo
          </button>
          <button
            onClick={redo}
            disabled={!canRedo}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg transition-colors ${
              canRedo 
                ? 'bg-white/10 text-white hover:bg-white/20' 
                : 'bg-white/5 text-white/30 cursor-not-allowed'
            }`}
          >
            <Redo2 className="w-4 h-4" />
            Redo
          </button>
        </div>

        {/* Publish/Discard buttons */}
        {pendingChanges.length > 0 && (
          <div className="flex gap-2">
            <button
              onClick={clearPendingChanges}
              className="flex-1 py-2.5 rounded-lg border border-white/20 text-white/70 hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Discard
            </button>
            <button
              onClick={handlePublish}
              disabled={isPublishing}
              className="flex-1 py-2.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isPublishing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Publish All
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Floating Admin Toolbar with Preview button
export const AdminToolbar = () => {
  const { 
    isAdmin, 
    isEditMode, 
    setEditMode, 
    pendingChanges,
    showPreview,
    setShowPreview,
    canUndo,
    canRedo,
    undo,
    redo,
  } = useAdmin();

  const [showUserMenu, setShowUserMenu] = useState(false);
  
  if (!isAdmin) return null;

  const handleSignOut = async () => {
    try {
      // Call logout API
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout API error:', err);
    }
    
    // Clear ALL local storage related to auth
    localStorage.removeItem('soiree_email');
    localStorage.removeItem('soiree_approved');
    localStorage.removeItem('frontrow_email');
    localStorage.removeItem('frontrow_approved');
    localStorage.removeItem('workshop_access');
    localStorage.removeItem('hthf_access');
    localStorage.removeItem('phygital_connected');
    
    // Force reload to reset all state
    window.location.href = '/';
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed right-4 top-1/2 -translate-y-1/2 z-40"
      >
        <div className="flex flex-col gap-2 p-2 rounded-2xl bg-black/90 backdrop-blur-md border border-white/20 shadow-2xl">
          {/* User Menu Toggle */}
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className={`p-3 rounded-xl transition-all ${
              showUserMenu 
                ? 'bg-green-500 text-white' 
                : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
            }`}
            title="Account"
            data-testid="admin-user-menu-toggle"
          >
            <User className="w-5 h-5" />
          </button>

          {/* Edit Mode Toggle */}
          <button
            onClick={() => setEditMode(!isEditMode)}
            className={`p-3 rounded-xl transition-all ${
              isEditMode 
                ? 'bg-purple-500 text-white' 
                : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
            }`}
            title={isEditMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
            data-testid="admin-edit-mode-toggle"
          >
            {isEditMode ? <Check className="w-5 h-5" /> : <Pencil className="w-5 h-5" />}
          </button>
          
          {isEditMode && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col gap-2"
            >
              {/* Preview Button */}
              <button
                onClick={() => setShowPreview(!showPreview)}
                className={`p-3 rounded-xl transition-all relative ${
                  showPreview
                    ? 'bg-blue-500 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
                title="Preview Changes"
                data-testid="admin-preview-toggle"
              >
                <Eye className="w-5 h-5" />
                {pendingChanges.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {pendingChanges.length}
                  </span>
                )}
              </button>

              {/* Undo Button */}
              <button
                onClick={undo}
                disabled={!canUndo}
                className={`p-3 rounded-xl transition-all ${
                  canUndo
                    ? 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                    : 'bg-white/5 text-white/30 cursor-not-allowed'
                }`}
                title="Undo"
              >
                <Undo2 className="w-5 h-5" />
              </button>

              {/* Redo Button */}
              <button
                onClick={redo}
                disabled={!canRedo}
                className={`p-3 rounded-xl transition-all ${
                  canRedo
                    ? 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                    : 'bg-white/5 text-white/30 cursor-not-allowed'
                }`}
                title="Redo"
              >
                <Redo2 className="w-5 h-5" />
              </button>

              <div className="px-2 py-1 text-[10px] text-white/50 text-center uppercase tracking-wider">
                Edit Mode
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* User Menu Popup */}
      <AnimatePresence>
        {showUserMenu && (
          <motion.div
            initial={{ opacity: 0, x: 20, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.95 }}
            className="fixed right-20 top-1/2 -translate-y-1/2 z-50"
          >
            <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden min-w-[200px]">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                <p className="text-xs text-gray-500">Signed in as admin</p>
              </div>
              <div className="p-2">
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid="admin-signout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm font-medium">Sign out</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview Panel */}
      <AnimatePresence>
        {showPreview && <PreviewPanel onClose={() => setShowPreview(false)} />}
      </AnimatePresence>
    </>
  );
};

// Editable Text Component with pending change tracking
export const EditableText = ({ 
  sectionId, 
  field, 
  defaultValue, 
  className = '',
  as: Component = 'span',
  multiline = false,
}) => {
  const { isAdmin, isEditMode, sectionContent, updateSectionContent, addPendingChange } = useAdmin();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [originalValue, setOriginalValue] = useState(defaultValue);
  const inputRef = useRef(null);

  // Get value from backend content - check both direct field and custom_fields
  // If no saved content exists, use the defaultValue prop
  const sectionData = sectionContent[sectionId] || {};
  const savedValue = sectionData[field] || sectionData.custom_fields?.[field];
  const displayValue = savedValue !== undefined && savedValue !== '' ? savedValue : defaultValue;

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (value !== originalValue) {
      // Track as pending change instead of immediate save
      addPendingChange(sectionId, field, originalValue, value);
      // Also save immediately for live preview
      await updateSectionContent(sectionId, { [field]: value });
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave();
    }
    if (e.key === 'Escape') {
      setValue(displayValue);
      setIsEditing(false);
    }
  };

  const handleStartEdit = () => {
    setValue(displayValue);
    setOriginalValue(displayValue);
    setIsEditing(true);
  };

  if (!isAdmin || !isEditMode) {
    return <Component className={className}>{displayValue}</Component>;
  }

  if (isEditing) {
    return (
      <div className="relative inline-block">
        {multiline ? (
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className={`${className} bg-purple-500/20 border-2 border-purple-500 rounded-lg px-2 py-1 focus:outline-none min-w-[200px]`}
            rows={3}
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className={`${className} bg-purple-500/20 border-2 border-purple-500 rounded-lg px-2 py-1 focus:outline-none min-w-[200px]`}
          />
        )}
      </div>
    );
  }

  return (
    <Component 
      className={`${className} cursor-pointer hover:bg-purple-500/20 rounded px-1 transition-colors relative group`}
      onClick={handleStartEdit}
    >
      {displayValue}
      <span className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <Pencil className="w-3 h-3 text-purple-400" />
      </span>
    </Component>
  );
};

// Editable Image Component with Lightbox Support
export const EditableImage = ({ 
  sectionId, 
  field, 
  defaultSrc, 
  className = '',
  alt = 'Image',
  style = {},
  aspectRatio = 'auto', // Can be 'auto', 'aspect-video', 'aspect-square', etc.
  objectFit = 'contain', // Can be 'contain', 'cover', 'fill', 'none'
}) => {
  const { isAdmin, isEditMode, sectionContent, updateSectionContent, addPendingChange } = useAdmin();
  const [isUploading, setIsUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' or 'library'
  const fileInputRef = useRef(null);

  const [imgError, setImgError] = useState(false);

  // Get image from backend content - check both direct field and custom_fields
  const sectionData = sectionContent[sectionId] || {};
  const imageSrc = sectionData[field] || sectionData.custom_fields?.[field] || defaultSrc;

  // Reset error state when image source changes
  useEffect(() => {
    setImgError(false);
  }, [imageSrc]);

  // Fetch media library when modal opens
  const fetchMediaLibrary = async () => {
    setLoadingLibrary(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/media-library`);
      if (response.ok) {
        const data = await response.json();
        setMediaLibrary(data.media || []);
      }
    } catch (error) {
      console.error('Failed to fetch media library:', error);
    }
    setLoadingLibrary(false);
  };

  const handleOpenModal = () => {
    setShowModal(true);
    fetchMediaLibrary();
  };

  const handleSelectFromLibrary = async (url) => {
    addPendingChange(sectionId, field, imageSrc, url);
    await updateSectionContent(sectionId, { [field]: url });
    console.log(`[EditableImage] Selected from library: ${field}`);
    setShowModal(false);
  };

  // Compress image on client side before uploading (avoids proxy body size limits)
  const compressImage = (file, maxWidth = 1920, quality = 0.80) => {
    return new Promise((resolve) => {
      // Skip compression for non-image files or SVGs
      if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
        resolve(file);
        return;
      }
      // Skip if already small enough (under 500KB)
      if (file.size < 500 * 1024) {
        resolve(file);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          // Scale down if needed
          if (width > maxWidth || height > maxWidth) {
            if (width > height) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            } else {
              width = Math.round((width * maxWidth) / height);
              height = maxWidth;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            if (blob && blob.size < file.size) {
              const compressed = new File([blob], file.name, { type: 'image/jpeg' });
              console.log(`[EditableImage] Compressed: ${(file.size/1024).toFixed(0)}KB → ${(blob.size/1024).toFixed(0)}KB`);
              resolve(compressed);
            } else {
              resolve(file); // Original is smaller, use as-is
            }
          }, 'image/jpeg', quality);
        };
        img.onerror = () => resolve(file); // Fallback to original
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Compress image to avoid proxy body size limits
      const processedFile = await compressImage(file);
      
      const formData = new FormData();
      formData.append('file', processedFile);
      
      // Get session token for authentication
      const sessionToken = localStorage.getItem('auth_session_token');
      const headers = {};
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      console.log(`[EditableImage] Uploading to: ${API_URL}/api/admin/upload (${(processedFile.size/1024).toFixed(0)}KB)`);
      const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        const imageUrl = data.url.startsWith('http') ? data.url : `${API_URL}${data.url}`;
        addPendingChange(sectionId, field, imageSrc, imageUrl);
        await updateSectionContent(sectionId, { [field]: imageUrl });
        console.log(`[EditableImage] Uploaded and saved: ${field} -> ${imageUrl}`);
        toast.success('Image uploaded and saved');
      } else {
        let errDetail = '';
        try {
          const errJson = await response.json();
          errDetail = errJson.detail || JSON.stringify(errJson);
        } catch {
          errDetail = await response.text().catch(() => '');
        }
        console.error(`[EditableImage] Upload failed: ${response.status} ${errDetail}`);
        toast.error(`Upload failed: ${errDetail || `Server error ${response.status}`}`);
      }
    } catch (error) {
      console.error('[EditableImage] Upload error:', error.message);
      // Show helpful error for CORS/network issues
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        toast.error(`Network error uploading image. Check CORS and backend URL (${API_URL})`);
      } else {
        toast.error(`Upload failed: ${error.message}`);
      }
    }
    setIsUploading(false);
    setShowModal(false);
  };

  // Lightbox for public viewing (click to expand)
  const Lightbox = () => (
    <AnimatePresence>
      {showLightbox && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm cursor-zoom-out"
          onClick={() => setShowLightbox(false)}
        >
          <motion.img
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.9 }}
            src={imageSrc}
            alt={alt}
            className="max-w-full max-h-full object-contain rounded-lg"
            style={{ maxHeight: '90vh', maxWidth: '90vw' }}
          />
          <button 
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full"
            onClick={() => setShowLightbox(false)}
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Public view - clickable image with lightbox
  if (!isAdmin || !isEditMode) {
    if (!imageSrc || imgError) {
      return (
        <div className={`${className} flex items-center justify-center bg-gray-100 rounded-lg`} style={style}>
          <ImageIcon className="w-8 h-8 text-gray-300" />
        </div>
      );
    }
    return (
      <>
        <img 
          src={imageSrc} 
          alt={alt} 
          className={`${className} cursor-zoom-in`}
          style={{ ...style, objectFit: objectFit }}
          onClick={() => setShowLightbox(true)}
          onError={() => setImgError(true)}
        />
        <Lightbox />
      </>
    );
  }

  // Admin edit view
  return (
    <div className="relative group w-full h-full">
      {imageSrc && !imgError ? (
        <img 
          src={imageSrc} 
          alt={alt} 
          className={className} 
          style={{ ...style, objectFit: objectFit }}
          onError={() => setImgError(true)}
        />
      ) : (
        <div className={`${className} flex items-center justify-center rounded-lg min-h-[60px]`} style={style}>
          <ImageIcon className="w-8 h-8 text-gray-300" />
        </div>
      )}
      
      {/* Edit overlay */}
      <div 
        className="absolute inset-0 bg-purple-500/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer rounded-lg"
        onClick={handleOpenModal}
      >
        <div className="bg-white rounded-lg p-2 shadow-lg">
          <ImageIcon className="w-6 h-6 text-purple-600" />
        </div>
      </div>

      {/* Upload Modal with Media Library */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="w-full max-w-2xl rounded-2xl p-6 bg-white max-h-[80vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Change Image</h3>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex gap-2 mb-4 border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('upload')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'upload' 
                      ? 'border-purple-500 text-purple-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Upload New
                </button>
                <button
                  onClick={() => setActiveTab('library')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'library' 
                      ? 'border-purple-500 text-purple-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Media Library ({mediaLibrary.length})
                </button>
              </div>

              {/* Upload Tab */}
              {activeTab === 'upload' && (
                <div>
                  {/* Current image preview */}
                  {imageSrc && (
                    <div className="mb-4 rounded-lg overflow-hidden bg-gray-100" style={{ maxHeight: '200px' }}>
                      <img src={imageSrc} alt="Current" className="w-full h-auto object-contain" style={{ maxHeight: '200px' }} />
                    </div>
                  )}

                  {/* Upload button */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="w-full py-4 px-4 rounded-xl border-2 border-dashed border-gray-300 hover:border-purple-400 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-purple-600"
                  >
                    {isUploading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        Upload New Image
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Media Library Tab */}
              {activeTab === 'library' && (
                <div className="flex-1 overflow-y-auto">
                  {loadingLibrary ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : mediaLibrary.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <ImageIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No images in library yet</p>
                      <p className="text-sm">Upload images to see them here</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-3">
                      {mediaLibrary.map((media, index) => (
                        <button
                          key={index}
                          onClick={() => handleSelectFromLibrary(media.url)}
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:border-purple-500 ${
                            imageSrc === media.url ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200'
                          }`}
                        >
                          <img 
                            src={media.url} 
                            alt={`Library ${index}`} 
                            className="w-full h-full object-cover"
                          />
                          {imageSrc === media.url && (
                            <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                              <Check className="w-6 h-6 text-purple-600 bg-white rounded-full p-1" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Editable Link Component
export const EditableLink = ({
  sectionId,
  urlField,
  textField,
  defaultUrl,
  defaultText,
  className = '',
  children,
}) => {
  const { isAdmin, isEditMode, sectionContent, updateSectionContent, addPendingChange } = useAdmin();
  const [showModal, setShowModal] = useState(false);
  const [url, setUrl] = useState(defaultUrl);
  const [text, setText] = useState(defaultText);

  const currentUrl = sectionContent[sectionId]?.[urlField] || url;
  const currentText = sectionContent[sectionId]?.[textField] || text;

  const handleSave = async () => {
    if (url !== currentUrl) {
      addPendingChange(sectionId, urlField, currentUrl, url);
    }
    if (text !== currentText) {
      addPendingChange(sectionId, textField, currentText, text);
    }
    await updateSectionContent(sectionId, { 
      [urlField]: url, 
      [textField]: text 
    });
    setShowModal(false);
  };

  if (!isAdmin || !isEditMode) {
    return (
      <a href={currentUrl} className={className}>
        {children || currentText}
      </a>
    );
  }

  return (
    <>
      <button
        onClick={() => { setUrl(currentUrl); setText(currentText); setShowModal(true); }}
        className={`${className} cursor-pointer hover:ring-2 hover:ring-purple-500 rounded transition-all relative group`}
      >
        {children || currentText}
        <span className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <LinkIcon className="w-3 h-3 text-purple-400" />
        </span>
      </button>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="w-full max-w-md rounded-2xl p-6 bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Link</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link Text</label>
                  <input
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2 px-4 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 py-2 px-4 rounded-lg bg-purple-600 text-white hover:bg-purple-700 flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Section Wrapper with edit indicator
export const EditableSection = ({ 
  sectionId, 
  children, 
  className = '' 
}) => {
  const { isAdmin, isEditMode } = useAdmin();

  if (!isAdmin || !isEditMode) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={`${className} relative`}>
      {/* Edit mode indicator */}
      <div className="absolute -top-3 left-4 z-10">
        <span className="px-2 py-1 text-[10px] font-medium bg-purple-500 text-white rounded-full uppercase tracking-wider">
          {sectionId}
        </span>
      </div>
      
      {/* Dashed border in edit mode */}
      <div className="border-2 border-dashed border-purple-500/30 rounded-lg">
        {children}
      </div>
    </div>
  );
};

export default {
  AdminProvider,
  AdminToolbar,
  PreviewPanel,
  EditableText,
  EditableImage,
  EditableLink,
  EditableSection,
  useAdmin,
};
